import {
  STATUSPAGE,
  SummaryOfTerminationTerms,
  TceSummaryBySituation,
} from '@repo/ui';
import {
  GetEntityTceCountUseCaseInterface,
  GetEntityTerminationTermCountUseCaseInterface,
} from 'domain/usecases';
import { RequestState } from 'presentation/app/common/interfaces/request-state';
import { useService } from 'presentation/app/hooks/useService';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { TcesTabSectionId } from '..';

interface IProps {
  entityId: string;
  changeSelectedTcesTabSectionId: (sectionId: TcesTabSectionId) => void;
}

export function SummariesSection({
  entityId,
  changeSelectedTcesTabSectionId,
}: IProps) {
  // Hooks
  const { entitiesService } = useService();

  // States
  const [getTceCountRequest, setGetTceCountRequest] = useState<
    RequestState<GetEntityTceCountUseCaseInterface.Result>
  >({
    data: undefined,
    status: STATUSPAGE.INITIAL,
  });
  const [getTerminationTermCountRequest, setGetTerminationTermCountRequest] =
    useState<
      RequestState<GetEntityTerminationTermCountUseCaseInterface.Result>
    >({
      data: undefined,
      status: STATUSPAGE.INITIAL,
    });

  // Methods
  const requestGetEntityTceCount = useCallback(
    (params: GetEntityTceCountUseCaseInterface.Params) => {
      setGetTceCountRequest((prevState) => ({
        ...prevState,
        status: STATUSPAGE.LOADING,
      }));
      entitiesService
        .getEntityTceCount(params)
        .then((res) => {
          setGetTceCountRequest((prevState) => ({
            ...prevState,
            data: res,
          }));
        })
        .catch((err) => {
          setGetTceCountRequest((prevState) => ({
            ...prevState,
            data: undefined,
          }));
        })
        .finally(() => {
          setGetTceCountRequest((prevState) => ({
            ...prevState,
            status: STATUSPAGE.DONE,
          }));
        });
    },
    [entityId],
  );

  const requestGetEntityTerminationTermCount = useCallback(
    (params: GetEntityTerminationTermCountUseCaseInterface.Params) => {
      setGetTerminationTermCountRequest((prevState) => ({
        ...prevState,
        status: STATUSPAGE.LOADING,
      }));
      entitiesService
        .getEntityTerminationTermCount(params)
        .then((res) => {
          setGetTerminationTermCountRequest((prevState) => ({
            ...prevState,
            data: res,
          }));
        })
        .catch((err) => {
          setGetTerminationTermCountRequest((prevState) => ({
            ...prevState,
            data: undefined,
          }));
        })
        .finally(() => {
          setGetTerminationTermCountRequest((prevState) => ({
            ...prevState,
            status: STATUSPAGE.DONE,
          }));
        });
    },
    [entityId],
  );

  const shouldRenderTceCountTable = useMemo(() => {
    if (!getTceCountRequest.data) return false;

    return !Object.values(getTceCountRequest?.data).every(
      (value) => value === 0,
    );
  }, [getTceCountRequest?.data]);

  const shouldRenderTerminationTermCountTable = useMemo(() => {
    if (!getTerminationTermCountRequest?.data) return false;

    return !Object.values(getTerminationTermCountRequest.data).every(
      (value) => value === 0,
    );
  }, [getTerminationTermCountRequest?.data]);

  const tceCountRows = useMemo(() => {
    return [
      {
        id: 'studentsWithoutTce',
        statusTitle: ' Estudantes sem TCE',
        statusSubtitle:
          'Estudantes ativos no grupo que ainda não iniciaram o processo de preenchimento do TCE.',
        icon: {
          bgColor: '#DDF6FB',
          class: 'ri-group-line',
          innerColor: '#1AB0CE',
        },
        count: getTceCountRequest?.data?.studentsWithoutTce ?? 0,
        onClick: () => {
          changeSelectedTcesTabSectionId('studentsWithoutTCE');
        },
      },
      {
        id: 'drafts',
        statusTitle: 'Rascunhos',
        statusSubtitle:
          'TCE iniciado pela instituição e pendente de preenchimento e/ou assinatura do estudante; ou TCE iniciado pelo estudante, aguardando finalização do preenchimento e/ou assinatura.',
        icon: {
          bgColor: '#E8ECF4 ',
          class: 'ri-sticky-note-line',
          innerColor: '#5A6583',
        },
        count: getTceCountRequest?.data?.drafts ?? 0,
        onClick: () => {
          changeSelectedTcesTabSectionId('drafts');
        },
      },
      {
        id: 'waitingStudentSignature',
        statusTitle: 'Aguardando Assinatura do Estudante',
        statusSubtitle:
          'TCE preenchido, mas aguardando assinatura(s) do Estudante.',
        icon: {
          bgColor: '#FDF1E9 ',
          class: 'ri-pencil-fill',
          innerColor: '#F19C59',
        },
        count: getTceCountRequest?.data?.waitingStudentSignature ?? 0,
        onClick: () => {
          changeSelectedTcesTabSectionId('waitingStudentSignature');
        },
      },
      {
        id: 'waitingGrantorSignature',
        statusTitle: 'Aguardando Assinatura do Concedente',
        statusSubtitle:
          'TCE preenchido, mas aguardando assinatura(s) do Concedente de Estágio ou Instituição de Ensino.',
        icon: {
          bgColor: '#FDF1E9 ',
          class: 'ri-pencil-line',
          innerColor: '#F19C59',
        },
        count: getTceCountRequest?.data?.waitingGrantorSignature ?? 0,
        onClick: () => {
          changeSelectedTcesTabSectionId('waitingGrantorSignature');
        },
      },
      {
        id: 'waitingInstitutionSignature',
        statusTitle: 'Aguardando Assinatura da Instituição',
        statusSubtitle:
          'TCE preenchido, mas aguardando assinatura(s) da Instituição de Ensino.',
        icon: {
          bgColor: '#FDF1E9 ',
          class: 'ri-edit-line',
          innerColor: '#F19C59',
        },
        count: getTceCountRequest?.data?.waitingInstitutionSignature ?? 0,

        onClick: () => {
          changeSelectedTcesTabSectionId('waitingInstitutionSignature');
        },
      },
      {
        id: 'integrative',
        statusTitle: 'Integradora',
        statusSubtitle:
          'TCE preenchido externamente, com posterior importação para o sistema Rade.',
        icon: {
          bgColor: '#FDF3D6',
          class: 'ri-file-text-line',
          innerColor: '#F2C94C',
        },
        count: getTceCountRequest?.data?.integrative ?? 0,
        onClick: () => {
          changeSelectedTcesTabSectionId('sentToIntegrative');
        },
      },
      {
        id: 'active',
        statusTitle: 'Ativos',
        statusSubtitle:
          'TCE concluído, assinado por todas as partes e em vigor, indicando que o estágio está em andamento.',
        icon: {
          bgColor: '#E0FBF4',
          class: 'ri-check-line',
          innerColor: '#27AE60',
        },
        count: getTceCountRequest?.data?.active ?? 0,
        onClick: () => {
          changeSelectedTcesTabSectionId('active');
        },
      },
      {
        id: 'expired',
        statusTitle: 'Expirados',
        statusSubtitle:
          'TCEs que já passaram da data de término, ou seja, não estão mais em vigor e não podem ser utilizados para validar estágios.',
        icon: {
          bgColor: '#FDEBEC',
          class: 'ri-alert-line',
          innerColor: '#EB5757',
        },
        count: getTceCountRequest?.data?.expired ?? 0,
        onClick: () => {
          changeSelectedTcesTabSectionId('expired');
        },
      },
    ];
  }, [getTceCountRequest?.data]);

  const terminationTermCountRows = useMemo(() => {
    return [
      {
        id: 'waitingGrantor',
        statusTitle: 'Aguardando Concedente',
        statusSubtitle:
          'termo(s) de rescisão aguardando ciência do concedente do estágio.',
        icon: {
          class: 'ri-edit-line',
        },
        count: getTerminationTermCountRequest?.data?.waitingGrantor ?? 0,
      },
      {
        id: 'waitingInstitution',
        statusTitle: 'Aguardando Instituição',
        statusSubtitle:
          'termo(s) de rescisão aguardando ciência da instituição.',
        icon: {
          class: 'ri-edit-box-line',
        },
        count: getTerminationTermCountRequest?.data?.waitingInstitution ?? 0,
      },
      {
        id: 'concluded',
        statusTitle: 'Concluídos',
        statusSubtitle: 'termo(s) de rescisão concluídos por todas as partes.',
        icon: {
          class: 'ri-check-double-line',
        },
        count: getTerminationTermCountRequest?.data?.concluded ?? 0,
      },
    ];
  }, [getTerminationTermCountRequest?.data]);

  useEffect(() => {
    requestGetEntityTceCount({
      entityId,
    });
    requestGetEntityTerminationTermCount({
      entityId,
    });
  }, [entityId]);

  const noDataSummaryConfig = {
    noDataTitle: 'Nenhum resumo de TCE encontrado',
    noDataDescription:
      'Use os filtros acima para pesquisar resumos de TCE por organização ou curso.',
  };

  const noDataTerminationTermConfig = {
    noDataTitle: 'Nenhum resumo de Termo de Rescisão encontrado',
    noDataDescription:
      'Use os filtros acima para pesquisar resumos de Termo de Rescisão por organização ou curso.',
  };

  return (
    <>
      <TceSummaryBySituation
        noDataTitle={noDataSummaryConfig.noDataTitle}
        noDataDescription={noDataSummaryConfig.noDataDescription}
        tceCountRows={tceCountRows}
        loading={getTceCountRequest.status}
        shouldRenderTceCountTable={shouldRenderTceCountTable}
      />
      <SummaryOfTerminationTerms
        noDataTitle={noDataTerminationTermConfig.noDataTitle}
        noDataDescription={noDataTerminationTermConfig.noDataDescription}
        loading={getTerminationTermCountRequest.status}
        terminationTermCountRows={terminationTermCountRows}
        shouldRenderTerminationTermCountTable={
          shouldRenderTerminationTermCountTable
        }
      />
    </>
  );
}
